import clsx from 'clsx';
import { ShieldCheck, ShieldAlert, ShieldOff, Info } from 'lucide-react';
import type { DataQualityReport } from '../../types';

interface DataQualityBadgeProps {
  quality: DataQualityReport;
  showDetails?: boolean;
  compact?: boolean;
}

export function DataQualityBadge({ quality, showDetails, compact }: DataQualityBadgeProps) {
  const score = quality.overallScore;
  const pct = Math.round(score * 100);

  const {
    color,
    bg,
    border,
    icon: Icon,
    label,
  } = score >= 0.75
    ? {
        color: 'text-win',
        bg: 'bg-win/10',
        border: 'border-win/30',
        icon: ShieldCheck,
        label: 'High',
      }
    : score >= 0.5
      ? {
          color: 'text-warn',
          bg: 'bg-warn/10',
          border: 'border-warn/30',
          icon: ShieldAlert,
          label: 'Medium',
        }
      : {
          color: 'text-loss',
          bg: 'bg-loss/10',
          border: 'border-loss/30',
          icon: ShieldOff,
          label: 'Low',
        };

  if (compact) {
    return (
      <span
        className={clsx(
          'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border',
          bg,
          border,
          color,
        )}
      >
        <Icon className="w-3 h-3" />
        {pct}%
      </span>
    );
  }

  return (
    <div className={clsx('rounded-lg border p-3', bg, border)}>
      <div className="flex items-center justify-between mb-2">
        <div className={clsx('flex items-center gap-1.5 text-sm font-semibold', color)}>
          <Icon className="w-4 h-4" />
          Data Integrity: {label} ({pct}%)
        </div>
        {quality.warnings.length > 0 && (
          <span className="text-xs text-gray-500">{quality.warnings.length} warning(s)</span>
        )}
      </div>

      {showDetails && (
        <>
          <div className="grid grid-cols-3 gap-2 text-xs mb-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-gray-500">Completeness</span>
              <span className={color}>{Math.round(quality.completenessScore * 100)}%</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-gray-500">Freshness</span>
              <span className={color}>{Math.round(quality.freshnessScore * 100)}%</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-gray-500">Reliability</span>
              <span className={color}>{Math.round(quality.reliabilityScore * 100)}%</span>
            </div>
          </div>

          {quality.warnings.length > 0 && (
            <div className="space-y-1 mt-2 pt-2 border-t border-surface-4">
              {quality.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs">
                  <Info
                    className={clsx(
                      'w-3 h-3 flex-shrink-0 mt-0.5',
                      w.severity === 'critical'
                        ? 'text-loss'
                        : w.severity === 'warning'
                          ? 'text-warn'
                          : 'text-info',
                    )}
                  />
                  <span className="text-gray-400">{w.message}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-2 pt-2 border-t border-surface-4">
            <div className="text-xs text-gray-500 mb-1">Sources:</div>
            {quality.sources.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-400">{s.name}</span>
                <span className={s.isStale ? 'text-warn' : 'text-gray-500'}>
                  {s.isStale ? 'Stale' : 'Fresh'} ·{' '}
                  {new Date(s.fetchedAt).toLocaleTimeString('en-US', {
                    timeZone: 'America/Los_Angeles',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
